/**
 * InfoTip.jsx : icône ⓘ qui ouvre une info-bulle (seuils de référence,
 * aide de la barre de période). Sort du flux de la page des textes de
 * rappel qui la chargeaient (10.5.313, D239).
 *
 * Ouverture au survol, au clic (épinglée jusqu'au clic suivant, au clic
 * ailleurs ou à Échap) et au clavier : bouton focusable, Entrée ou Espace
 * basculent, Échap ferme. Le contenu reste dans le DOM (attribut hidden)
 * pour qu'aria-controls désigne toujours un élément existant.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { getTheme, SHADOW } from '../constants/theme';

export default function InfoTip({ label, children, darkMode = false, testId }) {
  const theme = getTheme(darkMode);
  const contentId = useId();
  const wrapperRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovered || pinned;

  // Épinglée : un clic hors de l'info-bulle la ferme.
  useEffect(() => {
    if (!pinned) return undefined;
    const closeOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setPinned(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [pinned]);

  const closeOnEscape = (event) => {
    if (event.key !== 'Escape') return;
    setPinned(false);
    setHovered(false);
  };

  return (
    <span ref={wrapperRef} style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onKeyDown={closeOnEscape}>
      <button type="button" data-testid={testId} aria-label={label} aria-expanded={open}
        aria-controls={contentId} onClick={() => setPinned((value) => !value)}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '22px', height: '22px', padding: 0, borderRadius: '50%', cursor: 'pointer',
          border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textSecondary,
        }}>
        <Info size={14} aria-hidden="true" />
      </button>
      <span id={contentId} role="tooltip" data-testid={testId && `${testId}-content`} hidden={!open}
        style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 20,
          width: 'max-content', maxWidth: 'min(320px, calc(100vw - 48px))',
          padding: '10px 12px', borderRadius: '8px', boxShadow: SHADOW.elevated,
          background: theme.surfaceElevated, border: `1px solid ${theme.border}`,
          color: theme.textPrimary, fontSize: '12px', fontWeight: 400, lineHeight: 1.5,
          textAlign: 'left', whiteSpace: 'normal',
        }}>
        {children}
      </span>
    </span>
  );
}
